import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Scale, DollarSign, Users, ExternalLink, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import ThemeToggle from '../components/ThemeToggle';
import { useBrandSettings } from '../context/BrandSettingsContext';
import BrandLogo from '../components/BrandLogo';

const TransparencyPage = () => {
  const navigate = useNavigate();
  const { tagline, logos } = useBrandSettings();

  const policies = [
    {
      title: "Corrections & Complaints Policy",
      description: "How errors, complaints, and disputes relating to KaiCheckIt outputs are handled.",
      icon: FileText,
      path: "/transparency/corrections",
      color: "text-blue-600 dark:text-blue-400"
    },
    {
      title: "Methodology & Verification Policy",
      description: "Our structured, transparent methodology for responsible verification.",
      icon: Scale,
      path: "/transparency/methodology",
      color: "text-green-600 dark:text-green-400"
    },
    {
      title: "Funding & Financial Transparency",
      description: "Transparency regarding funding sources and financial independence.",
      icon: DollarSign,
      path: "/transparency/funding",
      color: "text-amber-600 dark:text-amber-400"
    },
    {
      title: "Independence & Impartiality Policy",
      description: "How KaiCheckIt operates independently of external influence.",
      icon: Users,
      path: "/transparency/independence",
      color: "text-purple-600 dark:text-purple-400"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <BrandLogo size="responsive" data-testid="brand-logo-transparency-header" />
            <span className="text-lg sm:text-xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-400 dark:to-blue-600 bg-clip-text text-transparent">
              KaiCheckIt
            </span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <ThemeToggle />
            <Button variant="outline" size="sm" onClick={() => navigate('/')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-4xl mx-auto">
          {/* Page Header */}
          <div className="text-center mb-10">
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-800 dark:text-slate-100 mb-4">
              Transparency & Accountability
            </h1>
            <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              KaiCheckIt is committed to the highest standards of transparency, accuracy, and accountability in AI-assisted verification.
            </p>
          </div>

          {/* Brand Statement */}
          <Card className="mb-8 bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 border-blue-200 dark:border-blue-700">
            <CardContent className="pt-6 text-center">
              <h2 className="text-xl font-bold text-blue-800 dark:text-blue-300 mb-2">
                {tagline}
              </h2>
              <p className="text-blue-700 dark:text-blue-400 italic">
                AI-assisted verification
              </p>
            </CardContent>
          </Card>

          {/* Policy Cards */}
          <div className="grid gap-4 sm:gap-6">
            {policies.map((policy, index) => (
              <Card 
                key={index}
                className="hover:shadow-lg transition-all cursor-pointer dark:bg-slate-800 dark:border-slate-700 group"
                onClick={() => navigate(policy.path)}
              >
                <CardHeader>
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-lg bg-slate-100 dark:bg-slate-700 ${policy.color}`}>
                      <policy.icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-lg sm:text-xl dark:text-slate-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                        {policy.title}
                      </CardTitle>
                      <CardDescription className="mt-1 dark:text-slate-400">
                        {policy.description}
                      </CardDescription>
                    </div>
                    <ExternalLink className="w-5 h-5 text-slate-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" />
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>

          {/* IFCN Declaration */}
          <Card className="mt-10 bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-slate-800 dark:text-slate-200 mb-2">
                    Our Commitment
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 italic">
                    "KaiCheckIt's policies and verification practices are designed to uphold the principles of transparency, accuracy, independence, and accountability."
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact */}
          <div className="mt-10 text-center">
            <p className="text-slate-600 dark:text-slate-400 mb-4">
              Questions about our policies or practices?
            </p>
            <Button variant="outline" onClick={() => navigate('/about')}>
              Contact Us
            </Button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-900/50 py-6 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-500 dark:text-slate-400">
          © 2025 Kaiventive Holdings. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default TransparencyPage;
